// Remove background step - processes all images to remove backgrounds.
//

import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";

import {
  initializeBackgroundRemoval,
  type ProgressCallback,
  removeBackground,
} from "@/site/tools";
import { ProcessingIndicator } from "@/site/ui/processing-indicator";
import { Button } from "@/ui/button";

import type { PhotoItem } from "../index";
import { createPreviewDataUrl } from "../utils/canvas";

interface RemoveBackgroundStepProps {
  photos: PhotoItem[];
  onComplete: (processedPhotos: Map<string, Blob>) => void;
  onBack: () => void;
}

// Remove background step component.
//
export function RemoveBackgroundStep({
  photos,
  onComplete,
  onBack,
}: RemoveBackgroundStepProps) {
  const [status, setStatus] = useState<"loading" | "processing" | "error">(
    "loading",
  );
  const [progress, setProgress] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [message, setMessage] = useState("Loading AI model...");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function process() {
      try {
        // Initialize the model with progress tracking.
        //
        const onProgress: ProgressCallback = (p) => {
          if (cancelled) return;

          if (p.status === "progress" && p.progress !== undefined) {
            setProgress(p.progress);
          } else if (p.status === "done") {
            setProgress(100);
          }
        };

        setMessage("Loading AI model (first time may take a moment)...");
        await initializeBackgroundRemoval(onProgress);

        if (cancelled) return;

        // Process all photos.
        //
        setStatus("processing");
        const results = new Map<string, Blob>();

        for (let i = 0; i < photos.length; i++) {
          if (cancelled) return;

          const photo = photos[i];
          if (!photo) continue;

          setCurrentPhotoIndex(i);
          setMessage(
            photos.length > 1
              ? `Removing background (${i + 1} of ${photos.length})...`
              : "Removing background...",
          );
          setProgress(0);

          // Create a data URL from the blob for processing.
          //
          const imageUrl = await createPreviewDataUrl(photo.originalImage);
          const result = await removeBackground(imageUrl);

          results.set(photo.id, result);
        }

        if (cancelled) return;

        onComplete(results);
      } catch (err) {
        if (cancelled) return;

        console.error("Background removal failed:", err);
        setStatus("error");
        setError(
          err instanceof Error ? err.message : "Failed to remove background",
        );
      }
    }

    void process();

    return () => {
      cancelled = true;
    };
  }, [photos, onComplete]);

  if (status === "error") {
    return (
      <div className="space-y-6 text-center">
        <div className="rounded-xl bg-red-50 p-6 dark:bg-red-950">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
            Something went wrong
          </h3>
          <p className="mt-2 text-red-700 dark:text-red-300">{error}</p>
        </div>

        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold">
          Removing Background{photos.length > 1 ? "s" : ""}
        </h3>
        <p className="text-muted-foreground mt-1">
          Please wait while we process your{" "}
          {photos.length > 1 ? `${photos.length} images` : "image"}.
        </p>
      </div>

      {/* Overall progress for multiple photos */}
      {photos.length > 1 && status === "processing" && (
        <div className="mx-auto max-w-xs">
          <div className="text-muted-foreground flex justify-between text-sm">
            <span>Overall progress</span>
            <span>
              {currentPhotoIndex + 1} of {photos.length}
            </span>
          </div>
          <div className="bg-accent mt-1 h-2 overflow-hidden rounded-full">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{
                width: `${((currentPhotoIndex + 1) / photos.length) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      <div className="py-8">
        <ProcessingIndicator
          message={message}
          progress={status === "loading" ? progress : undefined}
        />
      </div>

      <div className="text-center">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Cancel
        </Button>
      </div>
    </div>
  );
}
